import React, { useState, useEffect } from 'react';
import api from '../api';

const containerStyle = {
  padding: '1.5rem',
  backgroundColor: '#f8f9fa',
  minHeight: '100vh'
};

const headerStyle = {
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  padding: '2rem',
  borderRadius: '16px',
  textAlign: 'center',
  marginBottom: '2rem',
  boxShadow: '0 10px 40px rgba(102, 126, 234, 0.3)'
};

const tableContainerStyle = {
  backgroundColor: 'white',
  borderRadius: '16px',
  overflowX: 'auto',
  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
  border: '1px solid #e9ecef',
  marginBottom: '1rem'
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse'
};

const thStyle = {
  border: '1px solid #ddd',
  padding: '1rem',
  textAlign: 'center',
  backgroundColor: '#f8f9fa',
  color: '#333',
  fontWeight: 'bold'
};

const tdStyle = {
  border: '1px solid #ddd',
  padding: '1rem',
  textAlign: 'center',
  verticalAlign: 'middle'
};

const textTdStyle = {
  ...tdStyle,
  maxWidth: '150px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

const messageStyle = {
  backgroundColor: '#d4edda',
  border: '1px solid #c3e6cb',
  color: '#155724',
  padding: '1rem',
  borderRadius: '8px',
  marginBottom: '1rem'
};

const selectStyle = {
  padding: '0.5rem',
  borderRadius: '6px',
  border: '1px solid #ddd',
  backgroundColor: 'white',
  minWidth: '120px'
};

const saveButtonStyle = {
  backgroundColor: '#28a745',
  color: 'white',
  border: 'none',
  padding: '0.75rem 1rem',
  borderRadius: '8px',
  cursor: 'pointer',
  fontWeight: '600',
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 12px rgba(40, 167, 69, 0.3)'
};

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [passwordResets, setPasswordResets] = useState({});

  const fetchUsers = async () => {
    try {
      const res = await api.get('/admin/users');
      setUsers(res.data);
    } catch (err) {
      console.error('Fetch users error:', err);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleTypeChange = (id, value) => {
    setUsers(users.map(user => {
      if (user.id === id) {
        const newUser = { ...user, subscriptionType: value };
        if (value === 'premium') {
          const start = user.subscriptionStart ? new Date(user.subscriptionStart) : new Date();
          const expiry = new Date(start);
          const defaultTenure = user.subscriptionTenure > 0 ? user.subscriptionTenure : 30;
          expiry.setDate(start.getDate() + defaultTenure);
          newUser.subscriptionStart = start.toISOString();
          newUser.subscriptionExpiry = expiry.toISOString();
          newUser.subscriptionTenure = defaultTenure;
        } else {
          newUser.subscriptionTenure = 0;
          newUser.subscriptionStart = null;
          newUser.subscriptionExpiry = null;
        }
        return newUser;
      }
      return user;
    }));
  };

  const handleTenureChange = (id, value) => {
    setUsers(users.map(user => {
      if (user.id === id) {
        const start = user.subscriptionStart ? new Date(user.subscriptionStart) : new Date();
        const expiry = new Date(start);
        expiry.setDate(start.getDate() + Number(value));
        return {
          ...user,
          subscriptionTenure: value,
          subscriptionStart: start.toISOString(),
          subscriptionExpiry: expiry.toISOString()
        };
      }
      return user;
    }));
  };

  const handleSave = async (id) => {
    const user = users.find(u => u.id === id);
    try {
      const res = await api.post('/admin/userRole', {
        id,
        subscriptionType: user.subscriptionType || 'simple',
        subscriptionTenure: user.subscriptionTenure || 0
      });
      setMessage(res.data.message);
      fetchUsers();
    } catch (err) {
      setMessage(err.response?.data?.message || 'Update failed.');
      console.error('Update subscription error:', err);
    }
  };

  const handlePasswordResetChange = (id, field, value) => {
    setPasswordResets(current => ({
      ...current,
      [id]: { ...current[id], [field]: value }
    }));
  };

  const handlePasswordReset = async (id) => {
    const { password = '', confirmPassword = '' } = passwordResets[id] || {};
    if (password.length < 8) {
      setMessage('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setMessage('Passwords do not match.');
      return;
    }

    try {
      const res = await api.put(`/admin/users/${id}/password`, { password, confirmPassword });
      setPasswordResets(current => ({ ...current, [id]: { password: '', confirmPassword: '' } }));
      setMessage(res.data.message);
    } catch (err) {
      setMessage(err.response?.data?.message || 'Password reset failed.');
      console.error('Reset password error:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }
    try {
      const user = users.find(u => u.id === id);
      await api.delete(`/admin/users/${id}`);
      setMessage(`User ${user.username} deleted successfully.`);
      fetchUsers();
    } catch (err) {
      setMessage(err.response?.data?.message || 'Delete failed.');
      console.error('Delete user error:', err);
    }
  };

  const filteredUsers = users.filter(u =>
    u.phone.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h2 style={{ margin: 0, fontSize: '2rem' }}>User Management</h2>
      </div>
      {message && <div style={messageStyle}>{message}</div>}
      <div style={{ marginBottom: '1rem' }}>
        <input
          type="text"
          placeholder="Search by phone or email..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{
            padding: '0.75rem',
            borderRadius: '8px',
            border: '1px solid #ddd',
            width: '300px',
            fontSize: '1rem'
          }}
        />
      </div>
      <div style={tableContainerStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Phone</th>
              <th style={thStyle}>Email</th>
              <th style={thStyle}>Admin</th>
              <th style={thStyle}>Subscription</th>
              <th style={thStyle}>Duration (days)</th>
              <th style={thStyle}>Start</th>
              <th style={thStyle}>Expiry</th>
              <th style={thStyle}>Reset Password</th>
              <th style={thStyle}>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(u => {
              const expiryDate = u.subscriptionExpiry ? new Date(u.subscriptionExpiry) : null;
              const daysLeft = expiryDate ? Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24)) : null;
              return (
                <tr key={u.id} style={{ transition: 'background-color 0.3s ease' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  <td style={textTdStyle} title={u.phone}>{u.phone}</td>
                  <td style={textTdStyle} title={u.email}>{u.email}</td>
                  <td style={textTdStyle} title={u.phone}>{u.phone}</td>
                  <td style={tdStyle}>
                    <span style={{
                      backgroundColor: u.isAdmin ? '#28a745' : '#6c757d',
                      color: 'white',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      {u.isAdmin ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <select
                      value={u.subscriptionType || 'simple'}
                      onChange={e => handleTypeChange(u.id, e.target.value)}
                      style={selectStyle}
                    >
                      <option value="simple">Simple User</option>
                      <option value="premium">Premium User</option>
                    </select>
                  </td>
                  <td style={tdStyle}>
                    {u.subscriptionType === 'premium' ? (
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={u.subscriptionTenure || 30}
                        onChange={e => handleTenureChange(u.id, Number(e.target.value))}
                        style={{
                          ...selectStyle,
                          width: '80px',
                          textAlign: 'center'
                        }}
                        placeholder="Days"
                      />
                    ) : (
                      '—'
                    )}
                  </td>
                  <td style={tdStyle}>
                    {u.subscriptionStart ? new Date(u.subscriptionStart).toLocaleDateString() : '—'}
                  </td>
                  <td style={{
                    ...tdStyle,
                    color: daysLeft !== null && daysLeft <= 7 ? '#e74c3c' : '#28a745'
                  }}>
                    {expiryDate ? expiryDate.toLocaleDateString() : '—'}
                    {daysLeft !== null ? ` (${daysLeft} days left)` : ''}
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'grid', gap: '0.5rem', minWidth: '180px' }}>
                      <input
                        type="password"
                        autoComplete="new-password"
                        aria-label={`New password for ${u.phone}`}
                        placeholder="New password"
                        value={passwordResets[u.id]?.password || ''}
                        onChange={e => handlePasswordResetChange(u.id, 'password', e.target.value)}
                        style={{ ...selectStyle, width: '100%', boxSizing: 'border-box' }}
                      />
                      <input
                        type="password"
                        autoComplete="new-password"
                        aria-label={`Confirm new password for ${u.phone}`}
                        placeholder="Confirm password"
                        value={passwordResets[u.id]?.confirmPassword || ''}
                        onChange={e => handlePasswordResetChange(u.id, 'confirmPassword', e.target.value)}
                        style={{ ...selectStyle, width: '100%', boxSizing: 'border-box' }}
                      />
                      <button
                        type="button"
                        onClick={() => handlePasswordReset(u.id)}
                        style={{ ...saveButtonStyle, padding: '0.5rem' }}
                      >
                        Reset Password
                      </button>
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                      <button 
                        onClick={() => handleSave(u.id)}
                        style={saveButtonStyle}
                        onMouseEnter={e => {
                          e.target.style.backgroundColor = '#218838';
                          e.target.style.boxShadow = '0 6px 16px rgba(40, 167, 69, 0.4)';
                          e.target.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={e => {
                          e.target.style.backgroundColor = '#28a745';
                          e.target.style.boxShadow = '0 4px 12px rgba(40, 167, 69, 0.3)';
                          e.target.style.transform = 'scale(1)';
                        }}
                      >
                        Save
                      </button>
                      <button 
                        onClick={() => handleDelete(u.id)}
                        style={{
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          padding: '0.75rem 1rem',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          transition: 'all 0.3s ease',
                          boxShadow: '0 4px 12px rgba(220, 53, 69, 0.3)'
                        }}
                        onMouseEnter={e => {
                          e.target.style.backgroundColor = '#c82333';
                          e.target.style.boxShadow = '0 6px 16px rgba(220, 53, 69, 0.4)';
                          e.target.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={e => {
                          e.target.style.backgroundColor = '#dc3545';
                          e.target.style.boxShadow = '0 4px 12px rgba(220, 53, 69, 0.3)';
                          e.target.style.transform = 'scale(1)';
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminUsers;
